import { useState, useEffect } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import AlertService from "../../../services/alerts/alert.service";
import websiteConfigurationActions from "../../../store/actions/websiteConfiguration/websiteConfiguration.actions";
import "../../../styles/components/dialogs/websiteConfigurations/manageWebsiteConfigurationsDialogComponent.scss";
import { websiteConfigurationEnum } from "../../../enums/websiteConfigurations/websiteConfigurationType.enum";
import { homeAboutUsConfigurationModel } from "../../../models/websiteConfigurations/homeAboutUsConfiguration.model";
import WebsiteConfigurationHomeBannerSection from "./sections/WebsiteConfigurationHomeBannerSection";
import WebsiteConfigurationAboutSection from "./sections/WebsiteConfigurationAboutSection";
import WebsiteConfigurationAboutImageSection from "./sections/WebsiteConfigurationAboutImageSection";
import WebsiteConfigurationVatPercenteSection from "./sections/WebsiteConfigurationVatPercenteSection";
import WebsiteConfigurationDiscountPercenteSection from "./sections/WebsiteConfigurationDiscountPercenteSection";
import WebsiteConfigurationLogoSection from "./sections/WebsiteConfigurationLogoSection";
import { contactDetailsConfigurationModel } from "../../../models/websiteConfigurations/contactDetailsConfiguration.model";
import WebsiteConfigurationContactDetailsSection from "./sections/WebsiteConfigurationContactDetailsSection";

export const ManageWebsiteConfigurationsDialogComponent = ({ websiteConfigurationActions, configurations }) => {
  const [selectedConfiguration, setSelectedConfiguration] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      await websiteConfigurationActions.getAllConfigurations();
    } catch (error) {
      console.error("Error fetching configurations:", error);
    }
  };

  const handleFileChange = (file, fileDetails) => {
    setCurrentValue({ ...currentValue, file, Url: fileDetails.url });
  };

  const handleConfigurationChange = (event) => {
    const key = event.target.value;
    setSelectedConfiguration(key);

    const config = configurations.find((c) => c.key === key);
    if (config) setCurrentValue(JSON.parse(config.value));
    else {
      switch (key) {
        case websiteConfigurationEnum.HomeAboutUs:
          setCurrentValue({ ...homeAboutUsConfigurationModel });
          break;
        case websiteConfigurationEnum.ContactDetails:
          setCurrentValue({ ...contactDetailsConfigurationModel });
          break;
        default:
          setCurrentValue("");
      }
    }
  };

  const handleValueChange = (event) => {
    const { name, value } = event.target;

    switch (selectedConfiguration) {
      case websiteConfigurationEnum.HomeAboutUs:
      case websiteConfigurationEnum.ContactDetails:
        setCurrentValue({ ...currentValue, [name]: value });
        break;
      default:
        setCurrentValue(event.target.value);
        break;
    }
  };

  const handleUpdateLogo = async () => {
    try {
      const formData = new FormData();
      formData.append("file", currentValue.file);
      const response = await websiteConfigurationActions.uploadImage(selectedConfiguration, formData);
      AlertService.success("תמונה עודכנה בהצלחה");
      fetchConfigurations();
    } catch (error) {
      AlertService.error("שגיאה בעדכון תמונה. אנא נסה שנית מאוחר יותר");
      console.error("Update logo error:", error);
    }
  };

  const handleUpdateConfiguration = async () => {
    try {
      const request = {
        key: selectedConfiguration,
        value: JSON.stringify(currentValue),
      };
      await websiteConfigurationActions.addOrUpdateConfiguration(request);
      AlertService.success("הגדרות אתר עודכנו בהצלחה");
      fetchConfigurations();
    } catch (error) {
      AlertService.error("שגיאה בעדכון הגדרות אתר", "אנא נסה שנית מאוחר יותר");
      console.error("Update logo error:", error);
    }
  };

  const handleSave = async () => {
    try {
      if (!selectedConfiguration) {
        setErrors({ ...errors, general: "יש לבחור הגדרת אתר" });
        return;
      }

      if (selectedConfiguration === websiteConfigurationEnum.Logo || selectedConfiguration === websiteConfigurationEnum.HomeBanner || selectedConfiguration === websiteConfigurationEnum.HomeAboutUsImage) handleUpdateLogo();
      else handleUpdateConfiguration();
    } catch (error) {
      AlertService.error("שגיאה בשמירת הגדרת אתר", "אנא נסה שנית מאוחר יותר");
      console.error("Save configuration error:", error);
    }
  };

  const handleDeleteBanner = async (id) => {
    try {
      await websiteConfigurationActions.deleteHomeBanner(id);
      fetchConfigurations();
      AlertService.success("באנר נמחק בהצלחה");
    } catch (error) {
      AlertService.error("שגיאה במחיקת באנר", "אנא נסה שנית מאוחר יותר");
      console.error("Delete banner error:", error);
    }
  };

  const getConfigurationSelectElement = () => {
    return (
      <select value={selectedConfiguration} onChange={handleConfigurationChange}>
        <option value="">בחר הגדרות אתר</option>
        <option value={websiteConfigurationEnum.Logo}>לוגו</option>
        <option value={websiteConfigurationEnum.HomeBanner}>עמוד הבית - באנרים</option>
        <option value={websiteConfigurationEnum.HomeAboutUs}>עמוד הבית - קצת עלינו</option>
        <option value={websiteConfigurationEnum.HomeAboutUsImage}>עמוד הבית - קצת עלינו - תמונת רקע</option>
        <option value={websiteConfigurationEnum.VatPercent}>אחוז מע"מ</option>
        <option value={websiteConfigurationEnum.Discount}>הנחת אתר</option>
        <option value={websiteConfigurationEnum.ContactDetails}>פרטי יצירת קשר</option>
      </select>
    );
  };

  const renderConfigurationEditor = () => {
    switch (selectedConfiguration) {
      case websiteConfigurationEnum.Logo:
        return <WebsiteConfigurationLogoSection props={{ currentValue, handleChange: handleFileChange }} />;
      case websiteConfigurationEnum.HomeBanner:
        return <WebsiteConfigurationHomeBannerSection props={{ currentValue, handleChange: handleFileChange, handleDelete: handleDeleteBanner }} />;
      case websiteConfigurationEnum.HomeAboutUs:
        return <WebsiteConfigurationAboutSection props={{ currentValue, handleChange: handleValueChange }} />;
      case websiteConfigurationEnum.HomeAboutUsImage:
        return <WebsiteConfigurationAboutImageSection props={{ currentValue, handleChange: handleFileChange }} />;
      case websiteConfigurationEnum.VatPercent:
        return <WebsiteConfigurationVatPercenteSection props={{ currentValue, handleChange: handleValueChange }} />;
      case websiteConfigurationEnum.Discount:
        return <WebsiteConfigurationDiscountPercenteSection props={{ currentValue, handleChange: handleValueChange }} />;
      case websiteConfigurationEnum.ContactDetails:
        return <WebsiteConfigurationContactDetailsSection props={{ currentValue, handleChange: handleValueChange }} />;
      default:
        return <div>יש לבחור הגדרה מתוך הרשימה</div>;
    }
  };

  return (
    <div className="dialogContainer manageWebsiteConfigurationsContainer">
      <div className="contentContainer">
        <h1>ניהול הגדרות אתר</h1>
        {getConfigurationSelectElement()}
        {renderConfigurationEditor()}
      </div>
      <button className="primaryButton" onClick={handleSave}>
        שמור
      </button>
      {errors.general && <span className="error">{errors.general}</span>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  configurations: state.websiteConfigurationReducer.configurations,
});

const mapDispatchToProps = (dispatch) => {
  return {
    websiteConfigurationActions: bindActionCreators(websiteConfigurationActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageWebsiteConfigurationsDialogComponent);
